import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle, Info } from 'lucide-react';
import {
  useCreateTabConfiguration,
  useUpdateTabConfiguration,
  getStandardTabs,
  type TabConfiguration,
  type ConfiguredTab
} from '../../../services/queries/tabConfigQueries';
import { TabSelector } from './TabSelector';
import { useToast } from '../../../contexts/ToastContext';

interface TabConfigBuilderProps {
  config?: TabConfiguration;
  objectName: string;
  objectLabel: string;
  onClose: () => void;
}

export const TabConfigBuilder: React.FC<TabConfigBuilderProps> = ({
  config: initialConfig,
  objectName,
  objectLabel,
  onClose
}) => {
  const [configData, setConfigData] = useState<Partial<TabConfiguration>>({
    objectName,
    application: initialConfig?.application || undefined,
    tabs: initialConfig?.tabs || []
  });

  const [hasChanges, setHasChanges] = useState(false);

  const createConfigMutation = useCreateTabConfiguration();
  const updateConfigMutation = useUpdateTabConfiguration();
  const { success, error } = useToast();

  // Get all available tabs (standard + any custom ones)
  const allPossibleTabs: ConfiguredTab[] = [
    ...getStandardTabs().map((tab, index) => ({
      ...tab,
      isVisible: true,
      isDefault: index === 0,
      order: index
    })),
    // Add some custom tab examples
    { id: 'overview', label: 'Overview', source: 'custom' as const, isVisible: true, isDefault: false, order: 10 },
    { id: 'disputes', label: 'Disputes', source: 'related' as const, relatedObject: 'Dispute__c', isVisible: true, isDefault: false, order: 11 },
    { id: 'documents', label: 'Documents', source: 'related' as const, relatedObject: 'Document__c', isVisible: true, isDefault: false, order: 12 },
    { id: 'reports', label: 'Reports', source: 'custom' as const, isVisible: true, isDefault: false, order: 13 },
  ];

  // Separate active and available tabs
  const activeTabIds = new Set(configData.tabs?.map(t => t.id) || []);
  const availableTabs = allPossibleTabs.filter(t => !activeTabIds.has(t.id));
  const activeTabs = configData.tabs || [];

  useEffect(() => {
    if (initialConfig) {
      setConfigData({
        objectName: initialConfig.objectName,
        application: initialConfig.application,
        tabs: initialConfig.tabs
      });
    }
  }, [initialConfig]);

  const handleAddTab = (tab: ConfiguredTab) => {
    const newTab: ConfiguredTab = {
      ...tab,
      order: activeTabs.length
    };

    setConfigData(prev => ({
      ...prev,
      tabs: [...(prev.tabs || []), newTab]
    }));
    setHasChanges(true);
  };

  const handleRemoveTab = (tabId: string) => {
    setConfigData(prev => ({
      ...prev,
      tabs: prev.tabs?.filter(t => t.id !== tabId).map((t, index) => ({ ...t, order: index }))
    }));
    setHasChanges(true);
  };

  const handleToggleVisibility = (tabId: string) => {
    setConfigData(prev => ({
      ...prev,
      tabs: prev.tabs?.map(t =>
        t.id === tabId ? { ...t, isVisible: !t.isVisible } : t
      )
    }));
    setHasChanges(true);
  };

  const handleSetDefault = (tabId: string) => {
    setConfigData(prev => ({
      ...prev,
      tabs: prev.tabs?.map(t => ({
        ...t,
        isDefault: t.id === tabId
      }))
    }));
    setHasChanges(true);
  };

  const handleReorder = (tabId: string, direction: 'up' | 'down') => {
    const tabs = [...(configData.tabs || [])];
    const currentIndex = tabs.findIndex(t => t.id === tabId);

    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= tabs.length) return;

    // Swap
    [tabs[currentIndex], tabs[newIndex]] = [tabs[newIndex], tabs[currentIndex]];

    // Update order values
    tabs.forEach((tab, index) => {
      tab.order = index;
    });

    setConfigData(prev => ({ ...prev, tabs }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    // Validation
    if (!configData.tabs || configData.tabs.length === 0) {
      error('No tabs configured', 'Please add at least one tab');
      return;
    }

    const visibleTabs = configData.tabs.filter(t => t.isVisible);
    if (visibleTabs.length === 0) {
      error('No visible tabs', 'At least one tab must be visible');
      return;
    }

    const hasDefault = configData.tabs.some(t => t.isDefault);
    if (!hasDefault) {
      error('No default tab', 'Please set a default tab');
      return;
    }

    try {
      if (initialConfig) {
        await updateConfigMutation.mutateAsync({
          configId: initialConfig.id,
          updates: configData
        });
      } else {
        await createConfigMutation.mutateAsync(configData as Omit<TabConfiguration, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'lastModifiedBy'>);
      }
      success('Tab configuration saved successfully!');
      setHasChanges(false);
      onClose();
    } catch (err: any) {
      error('Failed to save tab configuration', err.message || 'Please try again');
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              {initialConfig ? 'Edit' : 'Create'} Tab Configuration
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              {objectLabel} • Configure visible tabs and their order
            </p>
          </div>

          <div className="flex items-center gap-3">
            {hasChanges && (
              <div className="flex items-center gap-2 text-sm text-yellow-600 dark:text-yellow-400">
                <AlertCircle className="w-4 h-4" />
                Unsaved changes
              </div>
            )}

            <button
              onClick={handleSave}
              disabled={!hasChanges || createConfigMutation.isPending || updateConfigMutation.isPending}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {createConfigMutation.isPending || updateConfigMutation.isPending ? 'Saving...' : 'Save Configuration'}
            </button>

            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Application Selection */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Application
              </h3>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Which application is this configuration for?
                </label>
                <select
                  value={configData.application || ''}
                  onChange={(e) => {
                    setConfigData(prev => ({
                      ...prev,
                      application: e.target.value || undefined
                    }));
                    setHasChanges(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={!!initialConfig && !initialConfig.application}
                >
                  <option value="">All Applications (Default)</option>
                  <option value="credit-repair">Credit Repair</option>
                  <option value="cybersweep">CyberSweep</option>
                  <option value="id-theft">ID Theft Recovery</option>
                  <option value="credit-development">Credit Development</option>
                  <option value="credit-block">Credit Block</option>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {!initialConfig?.application && initialConfig ?
                    'Default configuration cannot be changed to application-specific' :
                    'Select an application or leave blank for default configuration'}
                </p>
              </div>
            </div>

            {/* Instructions */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  <p className="font-semibold mb-2">How to configure tabs:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Add tabs from the "Available Tabs" panel to "Active Tabs"</li>
                    <li>Use the eye icon to show/hide tabs</li>
                    <li>Click the star icon to set the default tab (opens first)</li>
                    <li>Use arrows to reorder tabs</li>
                    <li>At least one tab must be visible and set as default</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Tab Selector */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Configure Tabs
              </h3>

              <TabSelector
                availableTabs={availableTabs}
                activeTabs={activeTabs}
                onAddTab={handleAddTab}
                onRemoveTab={handleRemoveTab}
                onToggleVisibility={handleToggleVisibility}
                onSetDefault={handleSetDefault}
                onReorder={handleReorder}
              />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                Configuration Summary
              </h4>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Total Tabs:</span>
                  <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                    {activeTabs.length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Visible Tabs:</span>
                  <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                    {activeTabs.filter(t => t.isVisible).length}
                  </span>
                </div>
                <div>
                  <span className="text-gray-600 dark:text-gray-400">Default Tab:</span>
                  <span className="ml-2 font-semibold text-gray-900 dark:text-white">
                    {activeTabs.find(t => t.isDefault)?.label || 'None'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
